import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, apiJson } from '../api';

// ============================================================
// Local types — mirror packages/shared/src/schemas/ppe-movement.ts.
// Inlined because the shared barrel index is owned by the Phase-3
// consolidation step. Same pattern as queries/fixed-assets.ts.
// ============================================================

export type PpeMovementSnapshotStatus = 'DRAFT' | 'FINALIZED';
export type PpeCategoryCode = 'LAND' | 'BUILDING' | 'MACHINERY' | 'VEHICLE' | 'COMPUTER' | 'OTHER';

export interface PpeMovementSnapshot {
  id: string;
  companyId: string;
  period: string;
  category: string;
  openingCost: string | number;
  additions: string | number;
  disposals: string | number;
  transfersIn: string | number;
  transfersOut: string | number;
  closingCost: string | number;
  openingAccDep: string | number;
  depCurrent: string | number;
  depReversal: string | number;
  closingAccDep: string | number;
  openingNbv: string | number;
  closingNbv: string | number;
  status: PpeMovementSnapshotStatus;
  generatedAt: string;
  generatedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GeneratePpeMovementResult {
  period: string;
  snapshotCount: number;
  skippedFinalized: number;
  snapshots: PpeMovementSnapshot[];
}

export interface PpeMovementMonthCell {
  period: string;
  closingNbv: string | number;
  closingCost: string | number;
  closingAccDep: string | number;
  status: PpeMovementSnapshotStatus | null;
}

export interface PpeMovementSummaryRow {
  category: string;
  months: PpeMovementMonthCell[];
}

export interface PpeMovementSummary {
  year: number;
  rows: PpeMovementSummaryRow[];
}

// Cache-key scope — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (matches fixed-assets / reclass conventions).
const snapKey = (companyId: string | undefined) => ['ppe-movement-snapshots', companyId] as const;
const summaryKey = (companyId: string | undefined) => ['ppe-movement-summary', companyId] as const;

export interface PpeMovementFilters {
  period?: string;
  category?: string;
  status?: PpeMovementSnapshotStatus;
  compute?: boolean;
}

export function usePpeMovementSnapshots(
  companyId: string | undefined,
  filters: PpeMovementFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.period) qs.set('period', filters.period);
  if (filters.category) qs.set('category', filters.category);
  if (filters.status) qs.set('status', filters.status);
  if (filters.compute) qs.set('compute', 'true');
  const search = qs.toString();
  return useQuery({
    queryKey: [...snapKey(companyId), filters],
    queryFn: () =>
      api<PpeMovementSnapshot[]>(
        `/companies/${companyId}/ppe-movement/snapshots${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function usePpeMovementSnapshot(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['ppe-movement-snapshots', companyId, id],
    queryFn: () => api<PpeMovementSnapshot>(`/companies/${companyId}/ppe-movement/snapshots/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function usePpeMovementSummary(companyId: string | undefined, year: number | undefined) {
  return useQuery({
    queryKey: [...summaryKey(companyId), year],
    queryFn: () =>
      api<PpeMovementSummary>(`/companies/${companyId}/ppe-movement/summary?year=${year}`),
    enabled: !!companyId && typeof year === 'number',
  });
}

export function useGeneratePpeMovement(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { period: string }) =>
      apiJson<{ period: string }, GeneratePpeMovementResult>(
        'POST',
        `/companies/${companyId}/ppe-movement/generate`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: snapKey(companyId) });
      void qc.invalidateQueries({ queryKey: summaryKey(companyId) });
    },
  });
}

export function useFinalizePpeMovementSnapshot(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, PpeMovementSnapshot>(
        'POST',
        `/companies/${companyId}/ppe-movement/snapshots/${id}/finalize`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: snapKey(companyId) });
      void qc.invalidateQueries({ queryKey: summaryKey(companyId) });
    },
  });
}
